import React, { useState, useRef, useEffect, createContext, useContext, FC, PropsWithChildren, RefObject } from 'react';
import styled from 'styled-components';

interface PopoverContextType {
    isOpen: boolean;
    toggle: () => void;
    popoverRef: RefObject<HTMLDivElement>;
}

export interface IPopoverBodyProps extends PropsWithChildren<unknown> {
    top?: string;
    right?: string;
    left?: string;
}

interface PopoverContentProps {
    top: string;
    right: string;
    left: string;
}

const PopoverContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const TriggerButton = styled.div`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    outline: none;
`;

const PopoverContent = styled.div<PopoverContentProps>`
    position: absolute;
    top: ${({ top }) => top};
    right: ${({ right }) => right};
    left: ${({ left }) => left};
    margin-top: 4px;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 100;
    width: max-content;
    min-width: 140px;
`;

const PopoverContext = createContext<PopoverContextType | undefined>(undefined);

export const Popover: FC<PropsWithChildren<unknown>> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggle = () => setIsOpen(prev => !prev);

    return (
        <PopoverContext.Provider value={{ isOpen, toggle, popoverRef }}>
            <PopoverContainer ref={popoverRef}>{children}</PopoverContainer>
        </PopoverContext.Provider>
    );
};

export const PopoverTrigger: FC<PropsWithChildren<unknown>> = ({ children }) => {
    const context = useContext(PopoverContext);

    if (!context) {
        throw new Error('PopoverTrigger must be used within a Popover');
    }

    const { toggle } = context;

    return <TriggerButton onClick={toggle}>{children}</TriggerButton>;
};

export const PopoverBody: FC<IPopoverBodyProps> = ({ children, top = '100%', right = '0', left = '0' }) => {
    const context = useContext(PopoverContext);

    if (!context) {
        throw new Error('PopoverBody must be used within a Popover');
    }

    const { isOpen, toggle } = context;

    if (!isOpen) {
        return null;
    }

    return (
        <PopoverContent top={top} right={right} left={left} onClick={toggle}>
            {children}
        </PopoverContent>
    );
};
